import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { storesApi, productsApi } from '@/services/api';
import { SeoMeta } from '@/components/seo/SeoMeta';
import { plainTextFromHtml, truncateMeta } from '@/utils/seo';
import { Breadcrumbs } from '@/components/shared/Breadcrumbs';
import { ProductCard } from '@/features/products/ProductCard';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Mail, Phone, Store as StoreIcon } from 'lucide-react';

export const StorePage = () => {
  const { id } = useParams();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [storeResponse, productsResponse] = await Promise.all([
          storesApi.getOne(id),
          productsApi.getAll({ store_id: id, limit: 12 })
        ]);
        setStore(storeResponse.data);
        setProducts(productsResponse.data.products);
      } catch (error) {
        console.error('Error fetching store:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="container-main py-8">
        <div className="animate-pulse">
          <div className="h-48 bg-gray-100 rounded-md mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-square bg-gray-100 rounded-md" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="container-main py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Butiken hittades inte</h1>
        <Link to="/produkter">
          <Button>Tillbaka till produkter</Button>
        </Link>
      </div>
    );
  }

  const breadcrumbs = [
    { label: 'Butiker', href: '/butiker' },
    { label: store.name }
  ];

  const storeDesc =
    truncateMeta(plainTextFromHtml(store.description)) ||
    truncateMeta(`Handla byggmaterial från ${store.name} på ByggFynd.`);

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="store-page">
      <SeoMeta
        title={store.name}
        description={storeDesc}
        canonicalPath={`/butik/${store.id}`}
        ogImage={store.logo_url || undefined}
      />
      <div className="container-main py-8">
        <Breadcrumbs items={breadcrumbs} />

        {/* Store Header */}
        <div className="bg-white rounded-md shadow-sm overflow-hidden mb-8">
          <div className="bg-[#003B73] h-32 md:h-48" />
          <div className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="w-24 h-24 md:w-32 md:h-32 bg-white rounded-md shadow-md -mt-16 md:-mt-24 border-4 border-white overflow-hidden flex-shrink-0">
                {store.logo_url ? (
                  <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <StoreIcon className="h-12 w-12 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-black text-[#0F172A] mb-2" data-testid="store-name">
                  {store.name}
                </h1>
                <p className="text-gray-600 mb-4">{store.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-[#003B73] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-[#0F172A]">Adress</p>
                      <p className="text-gray-600">{store.location}</p>
                      <p className="text-gray-500">{store.county}</p>
                    </div>
                  </div>
                  
                  {store.opening_hours && Object.keys(store.opening_hours).length > 0 && (
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-[#003B73] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-[#0F172A]">Öppettider</p>
                        {Object.entries(store.opening_hours).map(([day, hours]) => (
                          <p key={day} className="text-gray-600">
                            <span className="capitalize">{day}:</span> {hours}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex flex-col gap-3 md:w-48">
                {store.contact_email && (
                  <a href={`mailto:${store.contact_email}`}>
                    <Button variant="outline" className="w-full" data-testid="contact-email-btn">
                      <Mail className="h-4 w-4 mr-2" />
                      E-post
                    </Button>
                  </a>
                )}
                {store.contact_phone && (
                  <a href={`tel:${store.contact_phone}`}>
                    <Button className="w-full bg-[#003B73] hover:bg-[#002a52]" data-testid="contact-phone-btn">
                      <Phone className="h-4 w-4 mr-2" />
                      Ring
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Products */}
        <div>
          <h2 className="text-xl md:text-2xl font-black text-[#0F172A] mb-6">
            Produkter från {store.name}
          </h2>
          {products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-md">
              <p className="text-gray-500">Inga produkter finns just nu från denna butik.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
